// Watches the pill's calls and, once per call, asks the backend what to do
// when this user's outbound call ends (CU-869fmbgfn, contract V2-5 v1.1).
import { useEffect, useRef } from 'react';
import {
    BACKGROUND_TAB_DELAY_MS, OUTCOME_STATE, claimOutcome, fetchCallOutcome, readOutcome, wantsOutcomeCheck,
} from './callOutcome';
import { isFrontTab } from './callPillSync';

// "in_progress" right after the end event means the backend hasn't seen the
// end yet: look again a couple of times, then give up quietly.
export const IN_PROGRESS_RETRY_MS = [4000, 10000];

export default function useCallOutcomes({ calls, practiceId, skipAsk, onAutoLogged, onAsk }) {
    const checked = useRef(new Set());
    const timers = useRef([]);
    const practiceRef = useRef(practiceId);
    practiceRef.current = practiceId;
    // Latest handlers without re-running the watcher.
    const handlers = useRef({ onAutoLogged, onAsk, skipAsk });
    handlers.current = { onAutoLogged, onAsk, skipAsk };

    useEffect(() => {
        checked.current = new Set();
        return () => {
            timers.current.forEach(clearTimeout);
            timers.current = [];
        };
    }, [practiceId]);

    useEffect(() => {
        if (!practiceId) return;
        const check = async (call, attempt) => {
            const forPractice = practiceId;
            const outcome = readOutcome(await fetchCallOutcome(forPractice, call.callId));
            if (String(practiceRef.current) !== String(forPractice) || !outcome) return;
            if (outcome.state === OUTCOME_STATE.IN_PROGRESS && attempt < IN_PROGRESS_RETRY_MS.length) {
                timers.current.push(setTimeout(() => check(call, attempt + 1), IN_PROGRESS_RETRY_MS[attempt]));
                return;
            }
            const h = handlers.current;
            if (outcome.state === OUTCOME_STATE.AUTO_LOGGED && outcome.label) h.onAutoLogged(call, outcome);
            else if (outcome.state === OUTCOME_STATE.NEEDS_OUTCOME && outcome.options.length && !h.skipAsk?.(call)) h.onAsk(call, outcome);
        };
        // Only one tab asks / toasts: the front tab claims at once, others after a moment.
        const claimThenCheck = (call) => {
            if (String(practiceRef.current) !== String(practiceId)) return;
            if (claimOutcome(practiceId, call.callId)) check(call, 0);
        };
        Object.values(calls || {}).forEach((call) => {
            if (!wantsOutcomeCheck(call)) return;
            const id = String(call.callId);
            if (checked.current.has(id)) return;
            checked.current.add(id);
            if (isFrontTab()) claimThenCheck(call);
            else timers.current.push(setTimeout(() => claimThenCheck(call), BACKGROUND_TAB_DELAY_MS));
        });
    }, [calls, practiceId]);
}
